/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import { getCluProjectService } from 'src/clu/core/services/projects/cluProjectService';
import { cluProjectSwaggerToCluProjectStore } from 'src/clu/modules/handlers/projects/cluProjectMappers';
import { fileService } from 'src/core/services/api/fileService';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { AppStore } from 'src/core/stores/app/AppStore';
import { AppSettings, AzureAccount } from 'src/core/stores/app/types';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { RootStore } from 'src/core/stores/RootStore';
import { BaseEventParams } from 'src/core/types/eventBusEvent';
import { FileType } from 'src/core/types/FileType';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { batchPromises, getNextAbleResults } from 'src/core/utils/batchPromises';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { loadCustomTextProjects } from 'src/customText/modules/handlers/projects/customTextProjectHandlerGroup';
import { getAppVersionService } from 'src/luis/core/services/apps/appVersionService';
import { getLuisAppService } from 'src/luis/core/services/apps/luisAppService';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { appSettingsSwaggerToAppSettingsStore, luisAppSwaggerToLuisAppStore } from 'src/luis/modules/apps/handlers/luisAppMappers';
import { AppSwagger } from 'src/luis/projects/apps/types/swagger/AppSwagger';
import { LuisId } from 'src/luis/types/Resource';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { getQnaMakerProjectService } from 'src/qnaMaker/core/services/qnaMakerProjectService';
import { qnaMakerProjectSwaggerToStore } from 'src/qnaMaker/modules/handlers/project/qnaMakerProjectMappers';
import { Optional } from 'utility-types';

export type LuisAppHandlerGroup = ReturnType<typeof createLuisAppHandlerGroup>;
export type OpenLuisAppEvent = BaseEventParams<{ appId: string }, { appId: string }>;

/**
 * Loads the cultures for conversation apps.
 *
 * @param rootStore The root store that contains the app data.
 * @param params The params passed from the original caller.
 */
const loadConversationCultures = async (rootStore: RootStore, params: HandlerParams) => {
    const { context } = params;

    if (!context.forceRefresh && (rootStore.luisCultureStoreHouse.isLoading || rootStore.luisCultureStoreHouse.isLoaded)) {
        return;
    }

    try {
        rootStore.luisCultureStoreHouse.setIsLoading(true);

        const conversationAppCultures = await getLuisAppService({ context: { ...context, appType: 'conversation' } }).getAppCultures();
        rootStore.luisCultureStoreHouse.setItems(conversationAppCultures);

        rootStore.luisCultureStoreHouse.setIsLoaded(true);
        rootStore.luisCultureStoreHouse.setIsLoading(false);
    } catch (error) {
        rootStore.luisCultureStoreHouse.setIsLoaded(false);
    } finally {
        rootStore.luisCultureStoreHouse.setIsLoading(false);
    }
};

/**
 * Creates a handler group of functions for the apps to
 * be fired by the dispatcher.
 *
 * @link Check out dispatcher.md
 */
export const createLuisAppHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const logger = getLogger();
    const eventBus = getEventBusV2();

    /**
     * Loads apps for both documents and conversations.
     *
     * @param rootStore The root store that contains the app data.
     * @param params The params passed from the original caller.
     */
    const loadLuisApps = async (params?: HandlerParams<object>) => {
        const { context } = params;
        const { luisAppStoreHouse } = rootStore;
        const shouldLoadConversationApps = !luisAppStoreHouse.isLoading && !luisAppStoreHouse.isLoaded;

        if (!context.forceRefresh && !shouldLoadConversationApps) {
            return;
        }

        try {
            luisAppStoreHouse.setIsLoading(true);
            luisAppStoreHouse.tracker.setState('running');

            const conversationAppSwaggers = await getLuisAppService({ context }).getApps();
            luisAppStoreHouse.setItems(conversationAppSwaggers.map(luisAppSwaggerToLuisAppStore));

            luisAppStoreHouse.setIsLoading(false);
            luisAppStoreHouse.setIsLoaded(true);
            luisAppStoreHouse.tracker.setState('completed');
        } catch (error) {
            luisAppStoreHouse.setIsLoading(false);
            luisAppStoreHouse.tracker.setState('failed', <Error>error);

            const errorString: string = String(error);
            if (errorString.includes('PermissionDenied')) {
                rootStore.setHasPermissionToListLUISApps(false);
            }
            throw error;
        }
    };

    /**
     * Loads all apps and cultures.
     */
    const initializeApplicationsAndCultures = async (params?: HandlerParams) => {
        const paramsWithNoForceRefresh = { context: { ...params.context, forceRefresh: false } };

        rootStore.setCurrentLuisAppStore(null);

        (async () => await loadLuisApps(params))();
        (async () => await loadConversationCultures(rootStore, paramsWithNoForceRefresh))();
    };

    /**
     * Loads the given app's settings.
     */
    const loadAppSettings = async (params?: HandlerParams) => {
        const { context } = params;
        const appStore = rootStore.luisAppStoreHouse.findById(context.appId);

        if (!context.forceRefresh && appStore.appSettings) {
            return;
        }

        const appSettingsSwagger = await getLuisAppService({ context: { ...context, appType: appStore.appType } }).getAppSettings();

        appStore.setAppSettings(appSettingsSwaggerToAppSettingsStore(appSettingsSwagger));
    };

    /**
     * Updates the given app's settings.
     */
    const updateAppSettings = async ({ context, isPublic }: HandlerParams<Omit<AppSettings, 'id'>>) => {
        const appStore = rootStore.luisAppStoreHouse.findById(context.appId);

        await getLuisAppService({ context }).updateAppSettings({ isPublic });

        appStore.setAppSettings({ ...appStore.appSettings, isPublic });
    };

    /**
     * Updates an existing app.
     *
     * @param param0.name The new app name.
     * @param param0.description The new app description.
     */
    const renameApp = async ({ name, description, context }: HandlerParams<Pick<LuisAppStore, 'name' | 'description'>>) => {
        const appStoreToUpdate = rootStore.luisAppStoreHouse.findById(context.appId);

        await getLuisAppService({ context }).updateApp({ name, description });

        appStoreToUpdate.rename(name);
        appStoreToUpdate.setDescription(description);
    };

    /**
     * Deletes the apps with the given ids from server.
     *
     * @param param0.appIds The ids of the apps to be deleted.
     */
    const deleteApps = async ({ appIds, context }: HandlerParams<{ appIds: LuisId[] }>) => {
        const servicePromises = appIds.map(async appId => {
            const app = rootStore.luisAppStoreHouse.items.filter(a => a.id === appId)[0];
            const logTimedActivity = logger.createTimedActivityLogger('DeleteApp', (result, error) => {
                const logData = { appId: app.id, appName: app.name };

                return error ? logData : { ...logData, AppProperties: JSON.stringify(result) };
            });

            return logTimedActivity(getLuisAppService({ context: { ...context, appId } }).deleteApp());
        });

        await Promise.all(servicePromises);

        rootStore.luisAppStoreHouse.batchRemoveByIds(appIds);
    };

    /**
     * Sets the current opened app.
     * Loads the app's assigned azure Cs accounts.
     *
     * @param param0.id The id of the app to load accounts for.
     */
    const loadAppAzureCsAccounts = async (params?: HandlerParams) => {
        const { context } = params;

        const accounts = await getLuisAppService({ context }).getAppAzureCsAccounts();

        rootStore.luisAppStoreHouse.findById(context.appId).setAzureAccounts(accounts);
    };

    /**
     * Assigns the given Azure account to the given application.
     *
     * @param param0.id The id of the resource to add.
     * @param param0.path The path of the Azure resource.
     * @param param0.region The region of the Azure resource.
     */
    const assignAppAzureCsAccount = async ({ id, path, region, context }: HandlerParams<Partial<AzureAccount>>) => {
        const currentAppStore = rootStore.luisAppStoreHouse.findById(context.appId);

        await getLuisAppService({ context }).assignAppAzureCsAccount({ id, path, region });

        currentAppStore.setAzureAccounts([
            ...currentAppStore.azureAccounts,
            { id, path, region, accountName: '', kind: 'LUIS', resourceGroup: '' }
        ]);
    };

    /**
     * Un-assigns the given Azure account from the given application.
     *
     * @param param0.id The id of the resource to un-assign.
     */
    const unAssignAppAzureCsAccount = async ({ id, context }: HandlerParams<Pick<AzureAccount, 'id'>>) => {
        const currentAppStore = rootStore.luisAppStoreHouse.findById(context.appId);

        await getLuisAppService({ context }).unAssignAppAzureCsAccount({ id });

        currentAppStore.setAzureAccounts(currentAppStore.azureAccounts.filter(account => account.id !== id));
    };

    /**
     * Changes the assigned authoring cs account for the
     * given application.
     *
     * @param param0.csAccount The account to switch to.
     */
    const changeAppAuthoringCsAccount = async ({ csAccount, context }: HandlerParams<{ csAccount: AzureCsAccountStore }>) => {
        await getLuisAppService({ context }).changeAppAuthoringCsAccount({
            accountName: csAccount.name,
            resourceGroup: csAccount.resourceGroupName,
            azureSubscriptionId: csAccount.subscriptionId
        });

        rootStore.azureStore.session.setSubscriptionId(csAccount.subscriptionId);
        rootStore.azureStore.session.setCsAccountStore(csAccount);
    };

    const loadLanguageStudioProjects = async ({ localStore }: HandlerParams<{ localStore: ResourceStoreHouse<AppStore> }>) => {
        const { serviceKey } = rootStore.azureStore.session;
        const currentResource = rootStore.azureStore.session.csAccountStore;

        if (currentResource.kind !== 'TextAnalytics') {
            return;
        }

        localStore.setItems([]);
        localStore.tracker.setState('running');

        const callsBuffer = [
            getQnaMakerProjectService()
                .getProjects()
                .then(qnaApps => qnaApps.map(qnaMakerProjectSwaggerToStore))
                .then(qnaApps => localStore.batchAdd(qnaApps)),
            loadCustomTextProjects(serviceKey).then(customTextProjects =>
                localStore.batchAdd(
                    customTextProjects.map(p => new AppStore(p.id, p.projectKind, { name: p.name, modifiedDate: p.modifiedDate }))
                )
            ),
            getNextAbleResults(getCluProjectService().getProjects, 'value', [serviceKey])
                .then(projectSwaggers => projectSwaggers.map(cluProjectSwaggerToCluProjectStore))
                .then(projects =>
                    localStore.batchAdd(projects.map(p => new AppStore(p.id, p.projectType === 'Conversation' ? 'clu' : p.projectType, p)))
                )
        ];

        try {
            await batchPromises<number>(callsBuffer);

            localStore.tracker.setState('completed');
        } catch (e) {
            localStore.tracker.setState('failed');
        }
    };

    /**
     * Opens the conversation app with the given id in the context.
     * If the active version is null for any reason (backend issue),
     * fetch the versions and set the last modified version as the
     * active version.
     */
    const openConversationApp = async ({ context }: HandlerParams) => {
        const eventId = getId();
        let appStore = rootStore.luisAppStoreHouse.findById(context.appId);

        if (context.forceRefresh || !appStore) {
            try {
                appStore = luisAppSwaggerToLuisAppStore(await getLuisAppService({ context }).getAppById({ id: <string>context.appId }));
                appStore.setCsAccountPath(rootStore.azureStore.session.csAccountStore?.path);
                rootStore.luisAppStoreHouse.setItems([appStore]);
                rootStore.luisAppStoreHouse.setIsLoaded(false);
            } catch (e) {
                eventBus.emit<OpenLuisAppEvent>({ id: eventId, kind: 'openConversationApp', eventType: 'failed', error: <Error>e });

                return;
            }
        }

        try {
            const appVersionStores = await getAppVersionService({ context }).getAppVersions();
            appStore.appVersionStoreHouse.setItems(appVersionStores);

            if (!appStore.activeVersion) {
                const newActiveVersionId = appVersionStores.sort((a, b) => (a.modifiedDate < b.modifiedDate ? 1 : -1))[0].id;
                await getAppVersionService({ context }).activateAppVersion({ id: newActiveVersionId });
                appStore.setActiveVersion(newActiveVersionId);
            }

            rootStore.setCurrentLuisAppStore(appStore);
        } catch (e) {
            eventBus.emit<OpenLuisAppEvent>({ id: eventId, kind: 'openConversationApp', eventType: 'failed', error: <Error>e });

            return;
        }

        eventBus.emit<OpenLuisAppEvent>({
            id: eventId,
            kind: 'openConversationApp',
            eventType: 'completed',
            appId: <string>context.appId
        });
    };

    /**
     * Resets the current app.
     */
    const closeConversationApp = async () => rootStore.setCurrentLuisAppStore(null);

    /**
     * Creates a new app.
     *
     * @param param0.appDto New app data.
     */
    const createConversationApp = async ({
        name,
        description,
        culture,
        csAccount,
        context,
        tracker
    }: HandlerParams<Pick<LuisAppStore, 'name' | 'description' | 'culture'> & Optional<{ csAccount: AzureCsAccountStore }>>) => {
        const logTimedActivity = logger.createTimedActivityLogger<AppSwagger>('CreateConversationApp', (result, error) => {
            const logData = { appName: name };

            return error ? logData : { ...logData, appProperties: JSON.stringify({ appId: result.id }) };
        });

        const newAppStore = luisAppSwaggerToLuisAppStore(
            await logTimedActivity(getLuisAppService({ context }).createApp({ name, description, culture }))
        );

        rootStore.luisAppStoreHouse.add(newAppStore);

        if (csAccount) {
            await assignAppAzureCsAccount({
                id: csAccount.id,
                path: csAccount.path,
                region: csAccount.region,
                context: { ...context, appId: newAppStore.id }
            });
        }

        if (tracker) {
            tracker.setContext(newAppStore.id);
        }
    };

    /**
     * Imports the given app.
     *
     * @param param0.importedFile The imported JSON file.
     * @param param0.name The new name of the app.
     */
    const importConversationApp = async ({
        file,
        name,
        fileType = 'json',
        context,
        tracker
    }: HandlerParams<{ file: File; name: string; fileType?: FileType }>) => {
        const appFileString = await fileService.readFromDisk(file);

        const logTimedActivity = logger.createTimedActivityLogger<AppSwagger>('ImportConversationApp', (result, error) => {
            const logData = { appName: name };

            return error ? logData : { ...logData, appProperties: JSON.stringify(result) };
        });

        const app = luisAppSwaggerToLuisAppStore(
            await logTimedActivity(getLuisAppService({ context }).importApp({ appFileString, name, fileType }))
        );

        rootStore.luisAppStoreHouse.add(app);

        if (tracker) {
            tracker.setContext(app.id);
        }
    };

    /**
     * Exports the given app.
     */
    const exportConversationApp = async ({ fileType, context }: HandlerParams<{ fileType: FileType }>) => {
        let appStore = rootStore.luisAppStoreHouse.findById(context.appId);

        if (!appStore) {
            appStore = luisAppSwaggerToLuisAppStore(await getLuisAppService({ context }).getAppById({ id: <string>context.appId }));
        }

        /**
         * In the case where you export an app without knowing
         * the version in prior, the use the app active version.
         */
        if (!context.versionId) {
            context.versionId = appStore.activeVersion;
        }

        const logTimedActivity = logger.createTimedActivityLogger('ExportConversationApp', (result, error) => {
            const logData = { appName: appStore.name };

            return error ? logData : { ...logData, appProperties: JSON.stringify(result) };
        });

        await logTimedActivity(getLuisAppService({ context }).exportApp({ fileName: appStore.name, fileType }));
    };

    /**
     * Exports the given app.
     */
    const exportConversationAppForContainer = async ({ slot, context }: HandlerParams<{ slot: string }>) => {
        const logTimedActivity = logger.createTimedActivityLogger('ExportConversationAppForContainer', (result, error) =>
            error ? {} : { appProperties: JSON.stringify(result) }
        );
        await logTimedActivity(getLuisAppService({ context }).exportAppForContainer({ slot }));
    };

    /**
     * Imports a container endpoint logs for the given app.
     *
     * @param param0.logsCsvFile The selected CSV file.
     */
    const importConversationAppContainerLogs = async ({ logsCsvFile, context }: HandlerParams<{ logsCsvFile: File }>) => {
        const logsString = await fileService.readFromDisk(logsCsvFile);
        const formData = new FormData();
        formData.set('file', logsString);

        await getLuisAppService({ context }).importContainerEndpointLogs({ logsCsvFile: formData });
    };

    /**
     * Exports the endpoint logs of the given app.
     */
    const exportConversationAppLogs = async (params?: HandlerParams) => {
        const { context } = params;
        const appService = getLuisAppService({ context });

        await appService.initExportingEndpointLogs();
        await appService.checkExportingEndpointLogs({
            timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
            retryStrategy: response => response.status !== 201
        });
        await appService.exportEndpointLogs();
    };

    return {
        renameApp,
        deleteApps,
        loadLuisApps,
        loadAppSettings,
        updateAppSettings,
        openConversationApp,
        closeConversationApp,
        createConversationApp,
        exportConversationApp,
        importConversationApp,
        loadAppAzureCsAccounts,
        assignAppAzureCsAccount,
        unAssignAppAzureCsAccount,
        exportConversationAppLogs,
        loadLanguageStudioProjects,
        changeAppAuthoringCsAccount,
        exportConversationAppForContainer,
        initializeApplicationsAndCultures,
        importConversationAppContainerLogs
    };
};
